import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import {
  istProzentformat,
  istWaehrungsformat,
  kategorieDirektGelesen,
} from "@/office/excel/zahlenformat";
import { filledCells, isNumber, readWorkbook } from "@/office/excel/workbook";
import { KEIN_INHALT, nichtPruefbar } from "../schnittstelle";

export const lektionZahlenformate: Lesson = {
  id: "zahlenformate",
  title: "Zahlenformate: Anzeige und Wert sind zweierlei",
  summary: "Das Format ändert, was du siehst – nie, was in der Zelle steht. Diese Trennung ist Excels wichtigste Idee.",
  durationMinutes: 12,
  learningGoals: [
    "Zwischen dem gespeicherten Wert und seiner Anzeige unterscheiden",
    "Währungs- und Prozentformate gezielt einsetzen",
    "Erklären, warum eine Summe nicht zu ihren Einzelwerten passt",
  ],
  steps: [
    {
      id: "anzeige-und-wert",
      title: "Was du siehst, ist nicht was drinsteht",
      explanation: [
        "In der Zelle steht 1234,5678. Angezeigt wird 1.234,57 €. Beides ist richtig: " +
          "Der Wert ist die Zahl, die Anzeige ist das Zahlenformat. Excel rechnet immer " +
          "mit dem Wert, nie mit der Anzeige. In der Bearbeitungsleiste oben siehst du " +
          "deshalb immer den echten Inhalt, in der Zelle die formatierte Fassung.",
        "Aus dieser Trennung folgt das berühmteste Excel-Rätsel: Drei Zellen zeigen " +
          "je 0,33 €, die Summe darunter zeigt 1,00 € statt 0,99 €. Nichts ist kaputt – " +
          "in Wahrheit stehen dort dreimal 0,3333…, das Format rundet nur die Anzeige. " +
          "Die Summe rundet die echten 0,9999 auf 1,00.",
        "Wer wirklich runden will, muss den Wert runden, nicht die Anzeige: mit der " +
          "Funktion RUNDEN. Das ist eine bewusste Entscheidung und hat Konsequenzen, " +
          "denn danach ist die Nachkommastelle unwiederbringlich weg. Für Beträge, die " +
          "später auf eine Rechnung sollen, ist das genau richtig; für Zwischenwerte in " +
          "einer längeren Rechnung fast immer falsch.",
      ],
      keyPoints: [
        "Die Bearbeitungsleiste zeigt den Wert, die Zelle die Anzeige.",
        "Strg + 1 öffnet den Dialog „Zellen formatieren“.",
        "RUNDEN ändert den Wert, ein Zahlenformat nur die Anzeige.",
      ],
    },
    {
      id: "waehrung-setzen",
      title: "Einheiten gehören ins Format",
      explanation: [
        "Jetzt kommt die Einheit zurück, die im letzten Schritt aus dem Wert " +
          "verschwinden musste – aber an der richtigen Stelle. Das Währungsformat hängt " +
          "das Eurozeichen an die Anzeige, ohne den Wert anzufassen. Die Zelle zeigt " +
          "1.200,00 €, enthält aber weiterhin die reine Zahl 1200 und lässt sich " +
          "addieren, sortieren und in ein Diagramm ziehen.",
        "Zwei Formate stehen zur Auswahl und werden gern verwechselt. „Währung“ setzt " +
          "das Zeichen direkt vor oder hinter die Zahl. „Buchhaltung“ schiebt das " +
          "Zeichen an den linken Zellrand und richtet die Dezimaltrennzeichen " +
          "untereinander aus – deshalb sieht eine Spalte mit Buchhaltungsformat " +
          "ordentlicher aus, sobald die Beträge unterschiedlich lang sind.",
        "Für eigene Einheiten wie „kg“ oder „Stück“ gibt es das benutzerdefinierte " +
          "Format. Im Dialog unter „Benutzerdefiniert“ trägst du etwa 0\" kg\" ein: die " +
          "Null steht für die Zahl, der Text in Anführungszeichen wird angehängt. Die " +
          "Zelle zeigt „15 kg“, enthält aber die 15.",
      ],
      keyPoints: [
        "Strg + Umschalt + 4 setzt das Währungsformat, Strg + Umschalt + 5 Prozent.",
        "„Buchhaltung“ richtet Währungszeichen und Komma spaltenweise aus.",
        "Eigene Einheiten über „Benutzerdefiniert“, z. B. 0\" kg\".",
      ],
      task: {
        instruction:
          "Gib deiner Preis- oder Betragsspalte ein Währungsformat, sodass das " +
          "Eurozeichen sichtbar ist, ohne im Wert zu stehen.",
        hint: "Spalte markieren, dann Strg + Umschalt + 4 – oder Start → Zahlenformat → Währung.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Das Zahlenformat einer Zelle",
              "1.4",
              "Markiere deine Betragsspalte und sieh im Feld „Zahlenformat“ nach, ob dort „Währung“ steht.",
            );
          }

          const zahlen = filledCells(mappe).filter(isNumber);
          if (zahlen.length === 0) {
            return fail(
              KEIN_INHALT,
              "Trage zuerst Zahlen ein – die Aufgabe aus der vorigen Lektion.",
            );
          }

          const waehrung = zahlen.filter(istWaehrungsformat);
          if (waehrung.length === 0) {
            return fail(
              `Von ${zahlen.length} Zahlenzellen trägt keine ein Währungsformat.`,
              "Markiere die Spalte mit den Beträgen und drücke Strg + Umschalt + 4.",
            );
          }

          const ersatzweg = !kategorieDirektGelesen(waehrung[0]);
          return pass(
            `${waehrung.length} Zellen zeigen ihren Betrag mit Währungszeichen, ` +
              "enthalten aber weiterhin eine reine Zahl." +
              (ersatzweg
                ? " (Erkannt über die Formatzeichenfolge – diese Excel-Version meldet die " +
                  "Formatkategorie nicht direkt, die gibt es erst ab ExcelApi 1.12.)"
                : ""),
          );
        },
      },
    },
    {
      id: "prozent",
      title: "Prozent ist eine Multiplikation mit 100",
      explanation: [
        "Das Prozentformat ist die häufigste Stolperstelle unter den Zahlenformaten, " +
          "weil es zwei Dinge gleichzeitig tut: Es hängt ein Prozentzeichen an und " +
          "multipliziert die Anzeige mit 100. Der Wert 0,19 wird als 19 % angezeigt.",
        "Daraus folgt die Regel: Ein Prozentsatz wird als Dezimalzahl gespeichert. " +
          "Steht in der Zelle 19 und du weist danach das Prozentformat zu, zeigt Excel " +
          "1900 % – der Wert ist ja tatsächlich 19, also neunzehnhundert Prozent. " +
          "Tippst du dagegen in eine bereits als Prozent formatierte Zelle 19 ein, " +
          "versteht Excel das als 19 % und speichert 0,19.",
        "Für Rechnungen ist das ein Segen: Steht der Mehrwertsteuersatz als 0,19 in " +
          "einer Zelle, lautet die Formel schlicht =B7*B1 statt =B7*19/100. Und wenn " +
          "der Satz sich ändert, ändert sich genau eine Zelle.",
      ],
      keyPoints: [
        "Ein Prozentwert wird als Dezimalzahl gespeichert: 19 % = 0,19.",
        "Strg + Umschalt + 5 weist das Prozentformat zu.",
        "Erst formatieren, dann tippen – sonst multipliziert Excel mit 100.",
      ],
      task: {
        instruction:
          "Lege irgendwo in deiner Arbeitsmappe eine Zelle mit einem Prozentsatz an – " +
          "etwa einen Mehrwertsteuer-, Rabatt- oder Wachstumssatz – und gib ihr das " +
          "Prozentformat.",
        hint: "Zelle zuerst mit Strg + Umschalt + 5 formatieren, dann 19 eintippen. Excel speichert daraus 0,19.",
        check: async () => {
          const mappe = await readWorkbook();
          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Das Zahlenformat einer Zelle",
              "1.4",
              "Sieh nach, ob deine Prozentzelle das Prozentzeichen zeigt und in der Bearbeitungsleiste eine Dezimalzahl steht.",
            );
          }

          const zahlen = filledCells(mappe).filter(isNumber);
          const prozent = zahlen.filter(istProzentformat);
          const plausibel = prozent.filter(
            (zelle) => typeof zelle.value === "number" && Math.abs(zelle.value) <= 5,
          );

          return fromCriteria(
            [
              criterion(
                prozent.length > 0
                  ? `Prozentformat gefunden in ${prozent.map((zelle) => zelle.address).slice(0, 3).join(", ")}`
                  : "Noch keine Zelle mit Prozentformat",
                prozent.length > 0,
              ),
              criterion(
                plausibel.length > 0
                  ? "Der gespeicherte Wert ist eine Dezimalzahl, keine ganze Prozentzahl"
                  : prozent.length > 0
                    ? `Der Wert in ${prozent[0].address} ist ${prozent[0].value} – das wären ${Number(prozent[0].value) * 100} %`
                    : "Wert noch nicht prüfbar",
                plausibel.length > 0,
              ),
            ],
            {
              success:
                `Der Prozentsatz steht als Dezimalzahl in der Zelle und wird als ` +
                `${plausibel[0]?.text || "Prozentwert"} angezeigt – so lässt sich damit rechnen.`,
              failure: "Der Prozentsatz sitzt noch nicht richtig.",
              hint: "Formatiere die Zelle zuerst mit Strg + Umschalt + 5 und tippe danach die Zahl ein. " +
                "Umgekehrt macht Excel aus 19 die Anzeige 1900 %.",
            },
          );
        },
      },
    },
  ],
};
